/**
 * Texto de la política de privacidad.
 *
 * Vive aquí y no en los diccionarios de i18n porque es un documento largo con
 * estructura propia: meterlo en el JSON de traducciones lo volvería inmanejable
 * y mezclaría texto legal con cadenas de interfaz.
 *
 * Dos partes bien diferenciadas, y la distinción importa:
 *
 *  - Las secciones 1 a 3 son el texto oficial que facilita la Federació. Se
 *    reproduce sin cambios de contenido (solo se han corregido erratas
 *    tipográficas evidentes del original: "Federacó" por "Federació" y
 *    "seudonimització" por "pseudonimització").
 *
 *  - La sección 4 la añade el equipo de desarrollo y describe el tratamiento
 *    concreto que hace esta aplicación: qué datos se recogen, la ubicación
 *    durante el recorrido y la publicación del nombre del equipo en la
 *    clasificación. Es información factual sacada del propio código, y está
 *    porque la política general no cubre estas finalidades.
 *
 *    PENDIENTE: esta sección 4 tiene que revisarla y validarla la persona
 *    responsable de protección de datos de la Federació antes de publicar.
 *
 * El tipado con `satisfies` obliga a que los tres idiomas existan y tengan la
 * misma forma. La prueba de al lado comprueba además que tengan el mismo número
 * de secciones y de bloques, para que una traducción no se quede corta.
 */

import type { Idioma } from '@recorregut/shared';

export const RESPONSABLE = {
  nombre: "Federació d'Associacions, Entitats i Comissions d'Hostafrancs",
  direccion: 'c/ Sant Roc, 26-28, 1r pis, 08014 Barcelona',
  correo: 'comunicacio@federaciohostafrancs.cat',
  autoridadControl: 'https://www.aepd.es',
} as const;

export interface Bloque {
  tipo: 'parrafo' | 'lista';
  texto?: string;
  elementos?: string[];
}

export interface Seccion {
  titulo: string;
  bloques: Bloque[];
  /** Marca las secciones añadidas por desarrollo, que se señalan en pantalla. */
  anadidaPorDesarrollo?: boolean;
}

export interface Politica {
  titulo: string;
  intro: string;
  secciones: Seccion[];
  avisoSeccionAnadida: string;
  contactoTitulo: string;
  volver: string;
}

const ca: Politica = {
  titulo: 'Política de privacitat',
  intro:
    'Aquesta política explica com es tracten les dades personals que ens faciliteu en ' +
    "inscriure-us i en fer servir l'aplicació.",
  avisoSeccionAnadida:
    "Informació específica sobre el funcionament d'aquesta aplicació, complementària a la " +
    'política general.',
  contactoTitulo: 'Contacte',
  volver: 'Tornar',
  secciones: [
    {
      titulo: "1. Informació a l'usuari",
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            "La Federació d'Associacions, Entitats i Comissions d'Hostafrancs és el Responsable " +
            "del tractament de les dades personals de l'Usuari i l'informa que aquestes dades " +
            'seran tractades de conformitat amb el que disposa el Reglament (UE) 2016/679 de 27 ' +
            "d'abril de 2016 (RGPD), la Llei Orgànica 3/2018, de 5 de desembre, de Protecció de " +
            'Dades Personals i garantia dels drets digitals i normativa concordant, relatiu a la ' +
            'protecció de les persones físiques pel que fa al tractament de dades personals i a ' +
            "la lliure circulació d'aquestes dades, raó per la qual es facilita la següent " +
            'informació del tractament:',
        },
        {
          tipo: 'parrafo',
          texto:
            "Fi del tractament: mantenir una relació comercial amb l'Usuari. Les operacions " +
            'previstes per realitzar el tractament són:',
        },
        {
          tipo: 'lista',
          elementos: [
            'Remissió de comunicacions comercials publicitàries per email, fax, SMS, MMS, ' +
              'comunitats socials o qualsevol altre mitjà electrònic o físic, present o futur, ' +
              'que possibiliti realitzar comunicacions comercials. Aquestes comunicacions seran ' +
              'realitzades pel RESPONSABLE i relacionades sobre les seves activitats i les ' +
              'entitats membres de la Federació. En aquest cas, els tercers mai tindran accés a ' +
              'les dades personals.',
            "Tramitar encàrrecs de peticions o qualsevol tipus de petició que sigui realitzada " +
              "per l'usuari a través de qualsevol de les formes de contacte que es posen a la " +
              'seva disposició.',
            'Remetre el butlletí de notícies de la pàgina web.',
          ],
        },
        {
          tipo: 'parrafo',
          texto:
            'Criteris de conservació de les dades: es conservaran mentre hi hagi un interès mutu ' +
            'per mantenir la fi del tractament i quan ja no sigui necessari per a tal fi, es ' +
            'suprimiran amb mesures de seguretat adequades per garantir la pseudonimització de ' +
            'les dades o la destrucció total de les mateixes.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Comunicació de les dades: No es comunicaran les dades a tercers, excepte per ' +
            'obligació legal.',
        },
        { tipo: 'parrafo', texto: "Drets que assisteixen a l'Usuari:" },
        {
          tipo: 'lista',
          elementos: [
            'Dret a retirar el consentiment en qualsevol moment.',
            "Dret d'accés, rectificació, portabilitat i supressió de les seves dades i de la " +
              'limitació o oposició al seu tractament.',
            "Dret a presentar una reclamació davant l'autoritat de control (www.aepd.es) si " +
              "considera que el tractament no s'ajusta a la normativa vigent.",
          ],
        },
      ],
    },
    {
      titulo: "2. Caràcter obligatori o facultatiu de la informació facilitada per l'usuari",
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'Els Usuaris, mitjançant la marcació de les caselles corresponents i entrada de ' +
            'dades en els camps, marcats amb un asterisc (*) en el formulari de contacte o ' +
            'presentats en formularis de descàrrega, accepten expressament i de forma lliure i ' +
            'inequívoca, que les seves dades són necessàries per atendre la seva petició, per ' +
            "part del prestador, sent voluntària la inclusió de dades en els camps restants. " +
            "L'Usuari garanteix que les dades personals facilitades al RESPONSABLE són veraces i " +
            'es fa responsable de comunicar qualsevol modificació de les mateixes.',
        },
        {
          tipo: 'parrafo',
          texto:
            'El RESPONSABLE informa i garanteix expressament als usuaris que les seves dades ' +
            'personals no seran cedides en cap cas a tercers, i que sempre que realitzés algun ' +
            'tipus de cessió de dades personals, es demanarà prèviament el consentiment exprés, ' +
            'informat i inequívoc per part dels Usuaris. Totes les dades sol·licitades a través ' +
            "del lloc web són obligatòries, ja que són necessàries per a la prestació d'un " +
            'servei òptim a l\'usuari. En cas que no siguin facilitades totes les dades, no es ' +
            'garanteix que la informació i serveis facilitats siguin completament ajustats a les ' +
            'seves necessitats.',
        },
      ],
    },
    {
      titulo: '3. Mesures de seguretat',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'Que de conformitat amb el que disposen les normatives vigents en protecció de dades ' +
            'personals, el RESPONSABLE està complint amb totes les disposicions de les ' +
            'normatives RGPD per al tractament de les dades personals de la seva responsabilitat, ' +
            "i manifestament amb els principis descrits a l'article 5 del RGPD, pels quals són " +
            "tractats de manera lícita, lleial i transparent en relació amb l'interessat i " +
            'adequades, pertinents i limitats al que és necessari en relació amb els fins per als ' +
            'quals són tractats.',
        },
        {
          tipo: 'parrafo',
          texto:
            'El RESPONSABLE garanteix que ha implementat polítiques tècniques i organitzatives ' +
            'apropiades per aplicar les mesures de seguretat que estableixen el RGPD per tal de ' +
            'protegir els drets i llibertats dels Usuaris i els ha comunicat la informació ' +
            'adequada perquè puguin exercir-los.',
        },
      ],
    },
    {
      titulo: "4. Tractament específic d'aquesta aplicació",
      anadidaPorDesarrollo: true,
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            "En inscriure un equip en una gimcana es recullen les dades següents, amb la " +
            "finalitat de gestionar la vostra participació en l'esdeveniment:",
        },
        {
          tipo: 'lista',
          elementos: [
            "Nom de l'equip, que es mostra públicament a la classificació de l'esdeveniment.",
            'Nom de la persona responsable, telèfon i adreça electrònica, per contactar amb ' +
              "vosaltres si hi ha cap incidència i per enviar-vos el codi QR d'accés. Aquestes " +
              'dades no es mostren mai públicament.',
            "Nombre de persones de l'equip, només a efectes organitzatius.",
            "Idioma escollit, per enviar-vos les comunicacions en la vostra llengua.",
          ],
        },
        {
          tipo: 'parrafo',
          texto:
            "Ubicació durant el recorregut: mentre teniu l'aplicació oberta, el navegador " +
            "calcula la vostra posició per detectar quan arribeu a un punt. En el moment " +
            "d'arribar-hi, es desa al servidor la posició declarada i la seva precisió, " +
            "associades al vostre equip, per poder resoldre reclamacions sobre el resultat. " +
            "L'aplicació no fa cap seguiment continu del vostre recorregut ni desa la posició " +
            'mentre us desplaceu entre punts.',
        },
        {
          tipo: 'parrafo',
          texto:
            "Classificació pública: durant l'esdeveniment i un cop acabat, es publica una " +
            "classificació amb el nom de l'equip, la puntuació, els encerts i la durada del " +
            'recorregut. No hi apareix cap dada de contacte.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Conservació: les dades de contacte (persona responsable, telèfon i adreça ' +
            "electrònica) se suprimeixen automàticament un cop transcorregut el termini de " +
            "conservació establert des del final de l'esdeveniment. Es conserven el nom de " +
            "l'equip i el resultat, que ja no permeten identificar cap persona.",
        },
        {
          tipo: 'parrafo',
          texto:
            'Base jurídica: el vostre consentiment, que doneu marcant la casella del formulari ' +
            "d'inscripció i que podeu retirar en qualsevol moment escrivint a l'adreça de " +
            'contacte.',
        },
        {
          tipo: 'parrafo',
          texto:
            "Vídeos allotjats a YouTube: els vídeos dels punts són els de la sèrie #FemHistòria " +
            "publicats al canal de YouTube de la Federació. En reproduir-ne un, el vostre " +
            "navegador es connecta als servidors de Google, que poden recollir dades tècniques " +
            "com l'adreça IP. Fem servir el domini de YouTube sense galetes de seguiment per " +
            'reduir-ho al mínim, però la connexió amb aquest tercer existeix.',
        },
        {
          tipo: 'parrafo',
          texto:
            "Els menors d'edat no poden participar sols: han d'anar acompanyats de la persona " +
            "adulta responsable de l'equip, que és qui fa la inscripció i qui facilita les dades " +
            'de contacte. Per tant, no es recullen dades de contacte de cap menor.',
        },
      ],
    },
  ],
};

const es: Politica = {
  titulo: 'Política de privacidad',
  intro:
    'Esta política explica cómo se tratan los datos personales que nos facilitáis al ' +
    'inscribiros y al usar la aplicación.',
  avisoSeccionAnadida:
    'Información específica sobre el funcionamiento de esta aplicación, complementaria a la ' +
    'política general.',
  contactoTitulo: 'Contacto',
  volver: 'Volver',
  secciones: [
    {
      titulo: '1. Información al usuario',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'La Federació d\'Associacions, Entitats i Comissions d\'Hostafrancs es el Responsable ' +
            'del tratamiento de los datos personales del Usuario y le informa de que estos datos ' +
            'serán tratados de conformidad con lo dispuesto en el Reglamento (UE) 2016/679 de 27 ' +
            'de abril de 2016 (RGPD), la Ley Orgánica 3/2018, de 5 de diciembre, de Protección de ' +
            'Datos Personales y garantía de los derechos digitales y normativa concordante, ' +
            'relativo a la protección de las personas físicas en lo que respecta al tratamiento ' +
            'de datos personales y a la libre circulación de estos datos, razón por la cual se ' +
            'facilita la siguiente información del tratamiento:',
        },
        {
          tipo: 'parrafo',
          texto:
            'Fin del tratamiento: mantener una relación comercial con el Usuario. Las ' +
            'operaciones previstas para realizar el tratamiento son:',
        },
        {
          tipo: 'lista',
          elementos: [
            'Remisión de comunicaciones comerciales publicitarias por email, fax, SMS, MMS, ' +
              'comunidades sociales o cualquier otro medio electrónico o físico, presente o ' +
              'futuro, que posibilite realizar comunicaciones comerciales. Estas comunicaciones ' +
              'serán realizadas por el RESPONSABLE y relacionadas sobre sus actividades y las ' +
              'entidades miembros de la Federación. En este caso, los terceros nunca tendrán ' +
              'acceso a los datos personales.',
            'Tramitar encargos de peticiones o cualquier tipo de petición que sea realizada por ' +
              'el usuario a través de cualquiera de las formas de contacto que se ponen a su ' +
              'disposición.',
            'Remitir el boletín de noticias de la página web.',
          ],
        },
        {
          tipo: 'parrafo',
          texto:
            'Criterios de conservación de los datos: se conservarán mientras haya un interés ' +
            'mutuo para mantener el fin del tratamiento y cuando ya no sea necesario para tal ' +
            'fin, se suprimirán con medidas de seguridad adecuadas para garantizar la ' +
            'seudonimización de los datos o la destrucción total de los mismos.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Comunicación de los datos: No se comunicarán los datos a terceros, excepto por ' +
            'obligación legal.',
        },
        { tipo: 'parrafo', texto: 'Derechos que asisten al Usuario:' },
        {
          tipo: 'lista',
          elementos: [
            'Derecho a retirar el consentimiento en cualquier momento.',
            'Derecho de acceso, rectificación, portabilidad y supresión de sus datos y de la ' +
              'limitación u oposición a su tratamiento.',
            'Derecho a presentar una reclamación ante la autoridad de control (www.aepd.es) si ' +
              'considera que el tratamiento no se ajusta a la normativa vigente.',
          ],
        },
      ],
    },
    {
      titulo: '2. Carácter obligatorio o facultativo de la información facilitada por el usuario',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'Los Usuarios, mediante la marcación de las casillas correspondientes y entrada de ' +
            'datos en los campos, marcados con un asterisco (*) en el formulario de contacto o ' +
            'presentados en formularios de descarga, aceptan expresamente y de forma libre e ' +
            'inequívoca, que sus datos son necesarios para atender su petición, por parte del ' +
            'prestador, siendo voluntaria la inclusión de datos en los campos restantes. El ' +
            'Usuario garantiza que los datos personales facilitados al RESPONSABLE son veraces y ' +
            'se hace responsable de comunicar cualquier modificación de los mismos.',
        },
        {
          tipo: 'parrafo',
          texto:
            'El RESPONSABLE informa y garantiza expresamente a los usuarios que sus datos ' +
            'personales no serán cedidos en ningún caso a terceros, y que siempre que realizara ' +
            'algún tipo de cesión de datos personales, se pedirá previamente el consentimiento ' +
            'expreso, informado e inequívoco por parte de los Usuarios. Todos los datos ' +
            'solicitados a través del sitio web son obligatorios, ya que son necesarios para la ' +
            'prestación de un servicio óptimo al usuario. En caso de que no sean facilitados ' +
            'todos los datos, no se garantiza que la información y servicios facilitados sean ' +
            'completamente ajustados a sus necesidades.',
        },
      ],
    },
    {
      titulo: '3. Medidas de seguridad',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'Que de conformidad con lo dispuesto en las normativas vigentes en protección de ' +
            'datos personales, el RESPONSABLE está cumpliendo con todas las disposiciones de las ' +
            'normativas RGPD para el tratamiento de los datos personales de su responsabilidad, ' +
            'y manifiestamente con los principios descritos en el artículo 5 del RGPD, por los ' +
            'cuales son tratados de manera lícita, leal y transparente en relación con el ' +
            'interesado y adecuados, pertinentes y limitados a lo necesario en relación con los ' +
            'fines para los que son tratados.',
        },
        {
          tipo: 'parrafo',
          texto:
            'El RESPONSABLE garantiza que ha implementado políticas técnicas y organizativas ' +
            'apropiadas para aplicar las medidas de seguridad que establecen el RGPD con el fin ' +
            'de proteger los derechos y libertades de los Usuarios y les ha comunicado la ' +
            'información adecuada para que puedan ejercerlos.',
        },
      ],
    },
    {
      titulo: '4. Tratamiento específico de esta aplicación',
      anadidaPorDesarrollo: true,
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'Al inscribir un equipo en una gymkhana se recogen los datos siguientes, con la ' +
            'finalidad de gestionar vuestra participación en el evento:',
        },
        {
          tipo: 'lista',
          elementos: [
            'Nombre del equipo, que se muestra públicamente en la clasificación del evento.',
            'Nombre de la persona responsable, teléfono y dirección de correo, para contactar ' +
              'con vosotros si hay alguna incidencia y para enviaros el código QR de acceso. ' +
              'Estos datos no se muestran nunca públicamente.',
            'Número de personas del equipo, solo a efectos organizativos.',
            'Idioma escogido, para enviaros las comunicaciones en vuestra lengua.',
          ],
        },
        {
          tipo: 'parrafo',
          texto:
            'Ubicación durante el recorrido: mientras tenéis la aplicación abierta, el navegador ' +
            'calcula vuestra posición para detectar cuándo llegáis a un punto. En el momento de ' +
            'llegar, se guarda en el servidor la posición declarada y su precisión, asociadas a ' +
            'vuestro equipo, para poder resolver reclamaciones sobre el resultado. La aplicación ' +
            'no hace ningún seguimiento continuo de vuestro recorrido ni guarda la posición ' +
            'mientras os desplazáis entre puntos.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Clasificación pública: durante el evento y una vez terminado, se publica una ' +
            'clasificación con el nombre del equipo, la puntuación, los aciertos y la duración ' +
            'del recorrido. No aparece ningún dato de contacto.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Conservación: los datos de contacto (persona responsable, teléfono y dirección de ' +
            'correo) se suprimen automáticamente una vez transcurrido el plazo de conservación ' +
            'establecido desde el final del evento. Se conservan el nombre del equipo y el ' +
            'resultado, que ya no permiten identificar a ninguna persona.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Base jurídica: vuestro consentimiento, que dais marcando la casilla del formulario ' +
            'de inscripción y que podéis retirar en cualquier momento escribiendo a la dirección ' +
            'de contacto.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Vídeos alojados en YouTube: los vídeos de los puntos son los de la serie ' +
            '#FemHistòria publicados en el canal de YouTube de la Federació. Al reproducir uno, ' +
            'vuestro navegador se conecta a los servidores de Google, que pueden recoger datos ' +
            'técnicos como la dirección IP. Usamos el dominio de YouTube sin cookies de ' +
            'seguimiento para reducirlo al mínimo, pero la conexión con ese tercero existe.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Los menores de edad no pueden participar solos: deben ir acompañados de la persona ' +
            'adulta responsable del equipo, que es quien hace la inscripción y quien facilita ' +
            'los datos de contacto. Por lo tanto, no se recogen datos de contacto de ningún ' +
            'menor.',
        },
      ],
    },
  ],
};

const en: Politica = {
  titulo: 'Privacy policy',
  intro:
    'This policy explains how we process the personal data you give us when you register and ' +
    'when you use the app.',
  avisoSeccionAnadida:
    'Specific information about how this app works, supplementing the general policy.',
  contactoTitulo: 'Contact',
  volver: 'Back',
  secciones: [
    {
      titulo: '1. Information for the user',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            "Federació d'Associacions, Entitats i Comissions d'Hostafrancs is the Controller of " +
            'the User\'s personal data and informs the User that this data will be processed in ' +
            'accordance with Regulation (EU) 2016/679 of 27 April 2016 (GDPR), Organic Law ' +
            '3/2018 of 5 December on the Protection of Personal Data and guarantee of digital ' +
            'rights and related legislation, concerning the protection of natural persons with ' +
            'regard to the processing of personal data and on the free movement of such data. ' +
            'The following information about the processing is therefore provided:',
        },
        {
          tipo: 'parrafo',
          texto:
            'Purpose of the processing: to maintain a commercial relationship with the User. The ' +
            'operations envisaged for carrying out the processing are:',
        },
        {
          tipo: 'lista',
          elementos: [
            'Sending commercial advertising communications by email, fax, SMS, MMS, social ' +
              'networks or any other electronic or physical means, present or future, that makes ' +
              'commercial communications possible. These communications will be made by the ' +
              'CONTROLLER and will relate to its activities and to the member organisations of ' +
              'the Federation. In this case, third parties will never have access to the ' +
              'personal data.',
            'Processing orders or any kind of request made by the user through any of the ' +
              'contact methods made available.',
            'Sending the website newsletter.',
          ],
        },
        {
          tipo: 'parrafo',
          texto:
            'Data retention criteria: data will be kept as long as there is a mutual interest in ' +
            'maintaining the purpose of the processing, and when it is no longer necessary for ' +
            'that purpose it will be deleted with appropriate security measures to guarantee the ' +
            'pseudonymisation of the data or its total destruction.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Disclosure of data: data will not be disclosed to third parties, except where ' +
            'required by law.',
        },
        { tipo: 'parrafo', texto: 'Rights of the User:' },
        {
          tipo: 'lista',
          elementos: [
            'The right to withdraw consent at any time.',
            'The right of access, rectification, portability and erasure of their data, and to ' +
              'restrict or object to its processing.',
            'The right to lodge a complaint with the supervisory authority (www.aepd.es) if they ' +
              'consider that the processing does not comply with current legislation.',
          ],
        },
      ],
    },
    {
      titulo: '2. Mandatory or optional nature of the information provided by the user',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'By ticking the relevant boxes and entering data in the fields marked with an ' +
            'asterisk (*) in the contact form or in download forms, Users expressly, freely and ' +
            'unequivocally accept that their data is necessary for the provider to handle their ' +
            'request, the inclusion of data in the remaining fields being voluntary. The User ' +
            'guarantees that the personal data provided to the CONTROLLER is accurate and is ' +
            'responsible for reporting any changes to it.',
        },
        {
          tipo: 'parrafo',
          texto:
            'The CONTROLLER expressly informs and guarantees users that their personal data will ' +
            'not be transferred to third parties under any circumstances, and that should any ' +
            'transfer of personal data take place, the express, informed and unequivocal consent ' +
            'of the Users will be requested beforehand. All data requested through the website ' +
            'is mandatory, as it is necessary to provide an optimal service to the user. If not ' +
            'all data is provided, there is no guarantee that the information and services ' +
            'provided will fully match their needs.',
        },
      ],
    },
    {
      titulo: '3. Security measures',
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'In accordance with current personal data protection legislation, the CONTROLLER ' +
            'complies with all the provisions of the GDPR for the processing of the personal ' +
            'data under its responsibility, and manifestly with the principles set out in ' +
            'Article 5 of the GDPR, under which data is processed lawfully, fairly and ' +
            'transparently in relation to the data subject, and is adequate, relevant and ' +
            'limited to what is necessary in relation to the purposes for which it is processed.',
        },
        {
          tipo: 'parrafo',
          texto:
            'The CONTROLLER guarantees that it has implemented appropriate technical and ' +
            'organisational policies to apply the security measures established by the GDPR in ' +
            'order to protect the rights and freedoms of Users, and has provided them with ' +
            'adequate information so that they can exercise those rights.',
        },
      ],
    },
    {
      titulo: '4. Processing specific to this app',
      anadidaPorDesarrollo: true,
      bloques: [
        {
          tipo: 'parrafo',
          texto:
            'When you register a team for a trail, the following data is collected in order to ' +
            'manage your participation in the event:',
        },
        {
          tipo: 'lista',
          elementos: [
            'Team name, which is shown publicly on the event leaderboard.',
            'Name of the person in charge, phone number and email address, so we can contact you ' +
              'if there is a problem and send you your access QR code. This data is never shown ' +
              'publicly.',
            'Number of people in the team, for organisational purposes only.',
            'Chosen language, so we can write to you in your own language.',
          ],
        },
        {
          tipo: 'parrafo',
          texto:
            'Location during the trail: while you have the app open, the browser works out your ' +
            'position to detect when you reach a point. At the moment you arrive, the declared ' +
            'position and its accuracy are stored on the server, linked to your team, so that ' +
            'any complaint about the result can be resolved. The app does not continuously track ' +
            'your route and does not store your position while you move between points.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Public leaderboard: during the event and once it has finished, a leaderboard is ' +
            'published with the team name, score, correct answers and the duration of the trail. ' +
            'No contact details appear on it.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Retention: contact details (person in charge, phone and email address) are deleted ' +
            'automatically once the established retention period has passed since the end of the ' +
            'event. The team name and the result are kept, as they no longer identify any ' +
            'individual.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Legal basis: your consent, which you give by ticking the box on the registration ' +
            'form and which you can withdraw at any time by writing to the contact address.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Videos hosted on YouTube: the point videos are those of the #FemHistòria series ' +
            "published on the Federació's YouTube channel. When you play one, your browser " +
            'connects to Google servers, which may collect technical data such as your IP ' +
            'address. We use the cookie-free YouTube domain to keep this to a minimum, but the ' +
            'connection with that third party does exist.',
        },
        {
          tipo: 'parrafo',
          texto:
            'Minors may not take part on their own: they must be accompanied by the adult ' +
            'responsible for the team, who is the person making the registration and providing ' +
            'the contact details. No contact details of any minor are therefore collected.',
        },
      ],
    },
  ],
};

export const POLITICA = { ca, es, en } satisfies Record<Idioma, Politica>;
